import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, CheckCircle2, Loader2, AlertCircle, Navigation, LogOut, Camera, X, AlertTriangle, Clock, Send, User, CalendarOff, Users, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { attendanceAPI, lateSanctionAPI, authAPI, leaveAPI, locationAPI, sharedAPI } from '@/lib/api';
import { formatDate, formatTime, formatDateShort, getDeviceTimestamp } from '@/lib/dateUtils';
import { compressImage } from '@/lib/imageUtils';
import BottomNav from '@/components/BottomNav';
import ConsentModal from '@/components/ConsentModal';
import { useTranslation } from 'react-i18next';

export default function EmployeeHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState('');
  const [locLoading, setLocLoading] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkedOut, setCheckedOut] = useState(false);
  const [locError, setLocError] = useState('');
  const [todayRecord, setTodayRecord] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [lateStats, setLateStats] = useState(null);
  const [sanctionReason, setSanctionReason] = useState('');
  const [sanctionAttId, setSanctionAttId] = useState(null);
  const [sanctionLoading, setSanctionLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [checkoutSelfie, setCheckoutSelfie] = useState(null);
  const [showCheckoutCamera, setShowCheckoutCamera] = useState(false);
  const [checkoutLoc, setCheckoutLoc] = useState(null);
  const [locationLogs, setLocationLogs] = useState([]);
  const [logLocLoading, setLogLocLoading] = useState(false);
  const [logNote, setLogNote] = useState('');
  const [checkoutAddr, setCheckoutAddr] = useState('');
  const [checkoutLocLoading, setCheckoutLocLoading] = useState(false);
  const [checkinBlocked, setCheckinBlocked] = useState(null);
  const [checkinFailed, setCheckinFailed] = useState(null);
  const [showOnLeave, setShowOnLeave] = useState(false);
  const [onLeaveList, setOnLeaveList] = useState([]);
  const [onLeaveLoading, setOnLeaveLoading] = useState(false);
  const videoRef = useRef(null);
  const checkoutVideoRef = useRef(null);
  const streamRef = useRef(null);

  const fetchTodayStatus = useCallback(async () => {
    try {
      const res = await attendanceAPI.today();
      if (res.data && res.data.id) {
        setTodayRecord(res.data);
        if (res.data.all_completed) {
          // All sessions completed — employee can check in again
          setCheckedIn(false);
          setCheckedOut(false);
        } else if (res.data.checkout_timestamp) {
          setCheckedIn(true);
          setCheckedOut(true);
        } else {
          setCheckedIn(true);
          setCheckedOut(false);
        }
      }
    } catch {
      // Silent - no attendance today
    }
  }, []);

  const fetchLateStats = useCallback(async () => {
    try {
      const res = await attendanceAPI.myLateStats();
      setLateStats(res.data);
    } catch {
      // Silent
    }
  }, []);

  useEffect(() => {
    fetchTodayStatus();
    fetchLateStats();
    fetchLocationLogs();
    leaveAPI.myBalance().then(res => setLeaveBalance(res.data)).catch(e => console.error('Leave balance fetch failed:', e.message));
    attendanceAPI.canCheckin().then(res => {
      if (!res.data.allowed) setCheckinBlocked(res.data);
      else setCheckinBlocked(null);
    }).catch(e => console.error('Can-checkin fetch failed:', e.message));
    authAPI.getProfile().then(res => {
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      if (!res.data.consent_given && res.data.role === 'employee') {
        setShowConsent(true);
      }
    }).catch(e => console.error('Profile fetch failed:', e.message));
    return () => stopCamera();
  }, [fetchTodayStatus, fetchLateStats]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const getLocation = (retryCount = 0) => {
    setLocLoading(true);
    setLocError('');
    if (!navigator.geolocation) {
      setLocError('Geolocation not supported');
      setLocLoading(false);
      return;
    }
    const tryWithAccuracy = (highAccuracy) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setLocation(coords);
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18`
            );
            const data = await resp.json();
            setAddress(data.display_name || `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
          } catch {
            setAddress(`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`);
          }
          setLocLoading(false);
        },
        (err) => {
          if (highAccuracy && retryCount === 0) {
            // Retry with lower accuracy (cell tower/wifi) if GPS fails
            tryWithAccuracy(false);
          } else if (retryCount < 2) {
            // Auto-retry up to 2 times
            getLocation(retryCount + 1);
          } else {
            setLocError('Unable to get location. Please check your GPS settings and try again.');
            setLocLoading(false);
          }
        },
        { enableHighAccuracy: highAccuracy, timeout: 30000, maximumAge: 60000 }
      );
    };
    tryWithAccuracy(true);
  };

  const startCamera = async () => {
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 480, height: 480 }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      toast.error('Camera access denied');
      setShowCamera(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 240;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0, 240, 240);
    const raw = canvas.toDataURL('image/jpeg', 0.25);
    setSelfie(raw);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const handleCheckin = async () => {
    if (!location) {
      toast.error(t('attendance.fetch_location_first'));
      return;
    }
    setCheckinLoading(true);
    setCheckinFailed(null);
    try {
      const res = await attendanceAPI.checkin({
        latitude: location.lat,
        longitude: location.lng,
        address: address,
        selfie: selfie || null,
        device_timestamp: getDeviceTimestamp()
      });

      // Save local receipt as proof (immutable in localStorage)
      try {
        const receipt = {
          id: res.data.id,
          timestamp: new Date().toISOString(),
          latitude: location.lat,
          longitude: location.lng,
          address: address,
          date: res.data.date,
        };
        const receipts = JSON.parse(localStorage.getItem('checkin_receipts') || '[]');
        receipts.push(receipt);
        // Keep last 60 receipts
        localStorage.setItem('checkin_receipts', JSON.stringify(receipts.slice(-60)));
      } catch { /* localStorage not critical */ }

      // Post-check-in server verification
      try {
        const verify = await attendanceAPI.today();
        if (!verify.data?.id) {
          toast.error('Check-in may not be saved on server. Please check your history.', { duration: 15000 });
        }
      } catch { /* verification is best-effort */ }

      setCheckedIn(true);
      setTodayRecord(res.data);
      setCheckinBlocked(null);
      toast.success(t('attendance.attendance_success'));
      fetchLateStats();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const failInfo = {
        time: new Date().toLocaleTimeString(),
        error: detail || (err.code === 'ECONNABORTED' ? 'Request timed out' : 'Server error'),
        latitude: location.lat,
        longitude: location.lng,
        address: address,
      };
      setCheckinFailed(failInfo);
      // Also save failed attempt locally for evidence
      try {
        const fails = JSON.parse(localStorage.getItem('checkin_failures') || '[]');
        fails.push({ ...failInfo, timestamp: new Date().toISOString() });
        localStorage.setItem('checkin_failures', JSON.stringify(fails.slice(-30)));
      } catch { /* not critical */ }
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error(t('attendance.request_timeout'), { duration: 10000 });
      } else if (err.response?.status === 413) {
        toast.error(t('attendance.selfie_too_large'), { duration: 10000 });
      } else {
        toast.error(detail || t('attendance.failed_checkin'), { duration: 10000 });
      }
    } finally {
      setCheckinLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!checkoutLoc) {
      toast.error('Please fetch your location for checkout');
      return;
    }
    setCheckoutLoading(true);
    try {
      await attendanceAPI.checkout({
        latitude: checkoutLoc.lat,
        longitude: checkoutLoc.lng,
        address: checkoutAddr || '',
        selfie: checkoutSelfie || null,
        device_timestamp: getDeviceTimestamp()
      });
      toast.success(t('attendance.checkout_success'));
      // Reset state for potential re-check-in
      setCheckedIn(false);
      setCheckedOut(false);
      setCheckoutSelfie(null);
      setCheckoutLoc(null);
      setCheckoutAddr('');
      setSelfie(null);
      setLocation(null);
      setAddress('');
      fetchTodayStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check out');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getCheckoutLocation = (retryCount = 0) => {
    setCheckoutLocLoading(true);
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      setCheckoutLocLoading(false);
      return;
    }
    const tryWithAccuracy = (highAccuracy) => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCheckoutLoc(coords);
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18`
            );
            const data = await resp.json();
            setCheckoutAddr(data.display_name || `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
          } catch {
            setCheckoutAddr(`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`);
          }
          setCheckoutLocLoading(false);
        },
        (err) => {
          if (highAccuracy && retryCount === 0) {
            tryWithAccuracy(false);
          } else if (retryCount < 2) {
            getCheckoutLocation(retryCount + 1);
          } else {
            toast.error('Unable to get location. Please check GPS settings and try again.');
            setCheckoutLocLoading(false);
          }
        },
        { enableHighAccuracy: highAccuracy, timeout: 30000, maximumAge: 60000 }
      );
    };
    tryWithAccuracy(true);
  };

  const startCheckoutCamera = async () => {
    setShowCheckoutCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 480, height: 480 }
      });
      streamRef.current = stream;
      if (checkoutVideoRef.current) {
        checkoutVideoRef.current.srcObject = stream;
      }
    } catch {
      toast.error('Camera access denied');
      setShowCheckoutCamera(false);
    }
  };

  const captureCheckoutPhoto = () => {
    if (!checkoutVideoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = 240;
    canvas.height = 240;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(checkoutVideoRef.current, 0, 0, 240, 240);
    setCheckoutSelfie(canvas.toDataURL('image/jpeg', 0.25));
    stopCamera();
  };

  const fetchLocationLogs = async () => {
    try {
      const res = await locationAPI.myLogs();
      setLocationLogs(res.data || []);
    } catch { /* silent */ }
  };

  const handleLogLocation = async () => {
    setLogLocLoading(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        const tryGeo = (highAcc) => {
          navigator.geolocation.getCurrentPosition(resolve, (err) => {
            if (highAcc) tryGeo(false);
            else reject(err);
          }, { enableHighAccuracy: highAcc, timeout: 30000, maximumAge: 60000 });
        };
        tryGeo(true);
      });
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      let addr = '';
      try {
        const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
        const geoData = await geoRes.json();
        addr = geoData.display_name || '';
      } catch { /* silent */ }
      await locationAPI.log({ latitude: lat, longitude: lng, address: addr, note: logNote });
      toast.success('Location logged');
      setLogNote('');
      fetchLocationLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log location');
    } finally {
      setLogLocLoading(false);
    }
  };


  const fetchOnLeaveToday = async () => {
    setOnLeaveLoading(true);
    try {
      const res = await sharedAPI.onLeaveToday();
      setOnLeaveList(res.data.on_leave || []);
      setShowOnLeave(true);
    } catch {
      toast.error('Failed to load on-leave data');
    } finally {
      setOnLeaveLoading(false);
    }
  };

  const handleSanctionRequest = async (attId) => {
    if (!sanctionReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    setSanctionLoading(true);
    try {
      await lateSanctionAPI.request(attId, sanctionReason);
      toast.success('Sanction request submitted');
      setSanctionAttId(null);
      setSanctionReason('');
      fetchLateStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSanctionLoading(false);
    }
  };

  const isPending = user.status === 'pending';
  const isRejected = user.status === 'rejected';
  const greeting = new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening';

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#f4f4f5] pb-28">
      {/* Header with photo left, sign out right */}
      <div className="bg-[#026536] pt-10 pb-8 px-5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {user.profile_photo ? (
              <img src={user.profile_photo} alt={user.name} data-testid="header-photo" className="h-11 w-11 rounded-full object-cover border-2 border-white/30" />
            ) : (
              <div data-testid="header-photo-placeholder" className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center">
                <User className="text-white" size={20} />
              </div>
            )}
            <div>
              <p className="text-white/60 text-[10px] font-semibold uppercase tracking-wider">Good {greeting}</p>
              <h1 data-testid="employee-greeting" className="text-lg font-extrabold text-white tracking-tight leading-tight">{user.name || 'Employee'}</h1>
            </div>
          </div>
          <button data-testid="header-logout-btn" onClick={handleLogout} className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
            <LogOut size={16} className="text-white" />
          </button>
        </div>
        <div className="max-w-lg mx-auto mt-2">
          <Badge
            data-testid="account-status-badge"
            className={
              user.status === 'approved' ? 'bg-white/20 text-white border-0 text-xs' :
              user.status === 'pending' ? 'bg-[#dea439]/20 text-[#dea439] border-0 text-xs' :
              'bg-red-500/20 text-red-300 border-0 text-xs'
            }
          >
            {user.status === 'approved' ? 'Approved' : user.status === 'pending' ? 'Pending Approval' : 'Rejected'}
          </Badge>
        </div>
      </div>

      <div className="px-4 -mt-4 max-w-lg mx-auto space-y-4">
        {/* Status Messages */}
        {isPending && (
          <div data-testid="pending-notice" className="bg-[#dea439]/10 border border-[#dea439]/30 rounded-xl p-4 flex items-start gap-3 animate-slide-up">
            <AlertCircle className="text-[#dea439] shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-[#1a2529]">Account Pending</p>
              <p className="text-xs text-[#1a2529]/60 mt-0.5">Your account is awaiting admin approval.</p>
            </div>
          </div>
        )}

        {isRejected && (
          <div data-testid="rejected-notice" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-slide-up">
            <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-[#1a2529]">Account Rejected</p>
              <p className="text-xs text-[#1a2529]/60 mt-0.5">Contact your administrator for access.</p>
            </div>
          </div>
        )}

        {/* Late Analytics Card */}
        {lateStats && user.status === 'approved' && (
          <div data-testid="late-stats-card" className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#1a2529] flex items-center gap-2">
                <Clock size={15} className="text-[#dea439]" /> {t('late_sanction.monthly_summary')}
              </h3>
              <span className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">
                {new Date().toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-[#f4f4f5] rounded-xl p-3 text-center">
                <p className="text-xl font-extrabold text-[#1a2529]">{lateStats.total_present}</p>
                <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase mt-0.5">{t('attendance.present')}</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${lateStats.late_count > 0 ? 'bg-[#dea439]/10' : 'bg-[#026536]/10'}`}>
                <p data-testid="late-count" className={`text-xl font-extrabold ${lateStats.late_count > 0 ? 'text-[#dea439]' : 'text-[#026536]'}`}>{lateStats.late_count}</p>
                <p className={`text-[10px] font-semibold uppercase mt-0.5 ${lateStats.late_count > 0 ? 'text-[#dea439]/60' : 'text-[#026536]/60'}`}>{t('attendance.late')}</p>
              </div>
              <div className="bg-[#026536]/10 rounded-xl p-3 text-center">
                <p className="text-xl font-extrabold text-[#026536]">
                  {lateStats.total_present > 0 ? Math.round(((lateStats.total_present - lateStats.late_count) / lateStats.total_present) * 100) : 0}%
                </p>
                <p className="text-[10px] text-[#026536]/60 font-semibold uppercase mt-0.5">{t('attendance.on_time')}</p>
              </div>
            </div>
            {/* Late records with sanction request */}
            {lateStats.late_count > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] text-[#1a2529]/50 font-semibold">Late Arrivals:</p>
                {lateStats.late_records.map((r) => (
                  <div key={r.id} data-testid={`late-record-${r.date}`} className="bg-[#f4f4f5] rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-[10px]">
                          {formatDateShort(r.date)}
                        </Badge>
                        <span className="text-[11px] text-[#1a2529]/50">
                          {formatTime(r.timestamp)}
                        </span>
                      </div>
                      {r.sanction ? (
                        <Badge className={`text-[10px] border-0 ${r.sanction.status === 'approved' ? 'bg-[#026536]/10 text-[#026536]' : r.sanction.status === 'rejected' ? 'bg-red-50 text-red-500' : 'bg-[#dea439]/10 text-[#dea439]'}`}>
                          {r.sanction.status === 'approved' ? 'Sanctioned' : r.sanction.status === 'rejected' ? 'Denied' : 'Pending'}
                        </Badge>
                      ) : (
                        <button
                          data-testid={`request-sanction-${r.date}`}
                          onClick={() => setSanctionAttId(sanctionAttId === r.id ? null : r.id)}
                          className="text-[11px] text-[#026536] font-semibold hover:underline"
                        >
                          Request Sanction
                        </button>
                      )}
                    </div>
                    {/* Sanction request form */}
                    {sanctionAttId === r.id && (
                      <div className="mt-2 flex gap-2">
                        <input
                          data-testid="sanction-reason-input"
                          value={sanctionReason}
                          onChange={(e) => setSanctionReason(e.target.value)}
                          placeholder="Reason for late arrival..."
                          className="flex-1 text-xs border border-border/60 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-[#026536]/30"
                        />
                        <button
                          data-testid="submit-sanction-btn"
                          onClick={() => handleSanctionRequest(r.id)}
                          disabled={sanctionLoading}
                          className="h-8 px-3 bg-[#026536] text-white rounded-lg text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                        >
                          {sanctionLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                          Send
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leave Balance Widget */}
        {leaveBalance && user.status === 'approved' && (
          <div data-testid="leave-balance-card" className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#1a2529] flex items-center gap-2">
                <CalendarOff size={15} className="text-[#026536]" /> {t('leave.leave_balance')}
              </h3>
              <span className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">
                {leaveBalance.policy_type === 'monthly' ? 'This Month' : leaveBalance.year}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-[#026536]/5 rounded-xl p-3 text-center">
                <p className="text-lg font-extrabold text-[#026536]">{leaveBalance.total_available}</p>
                <p className="text-[9px] text-[#026536]/60 font-semibold uppercase mt-0.5">{t('leave.total')}</p>
              </div>
              <div className="bg-[#026536]/10 rounded-xl p-3 text-center">
                <p className="text-lg font-extrabold text-[#026536]">{leaveBalance.used}</p>
                <p className="text-[9px] text-[#026536]/60 font-semibold uppercase mt-0.5">{t('leave.used')}</p>
              </div>
              <div className="bg-[#dea439]/10 rounded-xl p-3 text-center">
                <p className="text-lg font-extrabold text-[#dea439]">{leaveBalance.pending}</p>
                <p className="text-[9px] text-[#dea439]/60 font-semibold uppercase mt-0.5">{t('common.pending')}</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${leaveBalance.remaining > 0 ? 'bg-[#026536]/5' : 'bg-red-50'}`}>
                <p className={`text-lg font-extrabold ${leaveBalance.remaining > 0 ? 'text-[#026536]' : 'text-red-500'}`}>{leaveBalance.remaining}</p>
                <p className={`text-[9px] font-semibold uppercase mt-0.5 ${leaveBalance.remaining > 0 ? 'text-[#026536]/60' : 'text-red-400'}`}>{t('leave.left')}</p>
              </div>
            </div>
            {leaveBalance.policy_type === 'cumulative' && leaveBalance.monthly_allowance > 0 && (
              <p className="text-[10px] text-[#1a2529]/30 mt-2 text-center">
                {leaveBalance.accumulated > 0 ? `${leaveBalance.accumulated} banked` : ''}
                {leaveBalance.accumulated > 0 && leaveBalance.monthly_allowance > 0 ? ' + ' : ''}
                {leaveBalance.monthly_allowance > 0 ? `${leaveBalance.monthly_allowance}/mo this month` : ''}
              </p>
            )}
            {leaveBalance.used_ytd > 0 && leaveBalance.used_ytd !== leaveBalance.used && (
              <p className="text-[10px] text-[#1a2529]/30 mt-1 text-center">
                Total used this year: {leaveBalance.used_ytd} day{leaveBalance.used_ytd !== 1 ? 's' : ''}
              </p>
            )}
            {leaveBalance.policy_type === 'monthly' && (
              <p className="text-[10px] text-[#1a2529]/30 mt-2 text-center">
                Monthly allowance: {leaveBalance.monthly_allowance} days (does not carry forward)
              </p>
            )}
            {(leaveBalance.lwp_used > 0 || leaveBalance.lwp_pending > 0) && (
              <div className="mt-2 bg-orange-50 rounded-lg px-3 py-1.5 text-center">
                <p className="text-[10px] font-semibold text-orange-600">
                  LWP: {leaveBalance.lwp_used} used{leaveBalance.lwp_pending > 0 ? `, ${leaveBalance.lwp_pending} pending` : ''}
                </p>
              </div>
            )}
            {leaveBalance.remaining === 0 && (
              <p className="text-[11px] text-orange-500 font-medium mt-2 text-center">No paid leaves remaining. New requests will be tagged as Leave Without Pay (LWP).</p>
            )}
          </div>
        )}

        {/* On Leave Today Button */}
        {user.status === 'approved' && (
          <button
            data-testid="on-leave-today-btn"
            onClick={fetchOnLeaveToday}
            disabled={onLeaveLoading}
            className="w-full bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between hover:bg-[#f4f4f5] transition-colors animate-slide-up"
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users size={16} className="text-purple-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold text-[#1a2529]">On Leave Today</p>
                <p className="text-[10px] text-[#1a2529]/40">See who's on leave</p>
              </div>
            </div>
            {onLeaveLoading ? <Loader2 size={16} className="animate-spin text-[#1a2529]/30" /> : <ChevronRight size={16} className="text-[#1a2529]/30" />}
          </button>
        )}

        {/* On Leave Today Modal */}
        {showOnLeave && (
          <div className="fixed inset-0 z-[1010] flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowOnLeave(false)}>
            <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[70vh] overflow-hidden animate-slide-up pb-20 sm:pb-0" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-border/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-purple-600" />
                  <h3 className="font-bold text-sm text-[#1a2529]">On Leave Today</h3>
                  <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">{onLeaveList.length}</Badge>
                </div>
                <button data-testid="close-on-leave-modal" onClick={() => setShowOnLeave(false)} className="h-7 w-7 rounded-full bg-[#f4f4f5] flex items-center justify-center">
                  <X size={14} className="text-[#1a2529]/60" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[55vh] p-4">
                {onLeaveList.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="mx-auto text-[#026536]/20 mb-2" size={28} />
                    <p className="text-sm font-medium text-[#1a2529]/40">No one is on leave today</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {onLeaveList.map((person, idx) => (
                      <div key={`${person.name}-${idx}`} data-testid={`on-leave-person-${idx}`} className="bg-[#f4f4f5] rounded-xl p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-600">
                            {person.name?.[0]?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#1a2529]">{person.name}</p>
                            <p className="text-[10px] text-[#1a2529]/40">
                              {person.role === 'employee' ? 'Staff' : person.role?.toUpperCase()}
                              {person.reporting_manager ? ` | RM: ${person.reporting_manager}` : ''}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-purple-50 text-purple-600 border-0 text-[10px]">{person.duration}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Persistent Check-in Failure Banner */}
        {checkinFailed && !checkedIn && (
          <div data-testid="checkin-failed-banner" className="bg-red-50 border-2 border-red-300 rounded-2xl p-4 animate-slide-up">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertCircle className="text-red-500" size={20} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-red-700 text-sm">Check-in Failed</p>
                <p className="text-xs text-red-600 mt-1">Your attendance was NOT recorded at {checkinFailed.time}.</p>
                <p className="text-xs text-red-500 mt-0.5">Reason: {checkinFailed.error}</p>
                <p className="text-[11px] text-red-400 mt-2">{checkinFailed.address} ({checkinFailed.latitude?.toFixed(4)}, {checkinFailed.longitude?.toFixed(4)})</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-xs font-semibold" onClick={() => { setCheckinFailed(null); }}>
                    Dismiss
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs font-semibold border-red-300 text-red-600"
                    onClick={() => { setCheckinFailed(null); handleCheckin(); }}>
                    Retry Check-in
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Previous Sessions Today */}
        {!checkedIn && todayRecord && todayRecord.all_completed && (
          <div data-testid="previous-sessions-card" className="bg-white rounded-2xl shadow-sm p-4 animate-slide-up">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-[#026536]" />
              <p className="text-xs font-bold text-[#1a2529]">
                {todayRecord.total_sessions_today} {t('attendance.session')}{todayRecord.total_sessions_today > 1 ? 's' : ''} {t('common.today').toLowerCase()}
              </p>
            </div>
            <p className="text-[11px] text-[#1a2529]/40">
              Last session: {formatTime(todayRecord.timestamp)} — {formatTime(todayRecord.checkout_timestamp)}
            </p>
            <p className="text-[11px] text-[#026536] font-medium mt-1">{t('attendance.can_checkin_again')}</p>
          </div>
        )}

        {/* Checked In + Checkout */}
        {checkedIn && todayRecord ? ( <>
          <div data-testid="checkin-success-card" className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-6 animate-slide-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full bg-[#026536]/10 flex items-center justify-center">
                <CheckCircle2 className="text-[#026536]" size={24} />
              </div>
              <div>
                <p className="font-bold text-[#1a2529]">
                  {t('attendance.checked_in')}
                  {todayRecord.session_number > 1 && (
                    <span className="ml-1.5 text-[10px] font-semibold text-[#026536] bg-[#026536]/10 px-1.5 py-0.5 rounded-full">{t('attendance.session')} {todayRecord.session_number}</span>
                  )}
                </p>
                <p className="text-xs text-[#1a2529]/50">
                  {formatTime(todayRecord.timestamp)}
                </p>
              </div>
              {todayRecord.is_late && (
                <Badge className="bg-[#dea439]/10 text-[#dea439] border-0 text-xs ml-auto">Late</Badge>
              )}
              {todayRecord.is_wfh && (
                <Badge data-testid="wfh-badge" className="bg-violet-50 text-violet-600 border-0 text-xs ml-auto">WFH</Badge>
              )}
            </div>
            <div className="bg-[#f4f4f5] rounded-xl p-3 space-y-2">
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-[#026536] mt-0.5 shrink-0" />
                <p className="text-xs text-[#1a2529]/70 leading-relaxed">{todayRecord.address || `${todayRecord.latitude}, ${todayRecord.longitude}`}</p>
              </div>
              <div className="flex items-center gap-2">
                <Navigation size={14} className="text-[#026536] shrink-0" />
                <p className="text-xs text-[#1a2529]/50">{todayRecord.latitude?.toFixed(6)}, {todayRecord.longitude?.toFixed(6)}</p>
              </div>
              <div className="flex items-center gap-2 pt-1 border-t border-[#1a2529]/5">
                <CheckCircle2 size={12} className="text-[#026536] shrink-0" />
                <p data-testid="receipt-id" className="text-[10px] font-mono text-[#1a2529]/40 select-all">Receipt: {todayRecord.id?.slice(0, 8).toUpperCase()}</p>
              </div>
            </div>

            {/* Geofence Warning */}
            {todayRecord.is_outside_zone && (
              <div data-testid="geofence-warning" className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
                <div>
                  <p className="text-xs font-semibold text-red-600">Outside Assigned Zone</p>
                  <p className="text-[10px] text-red-400 mt-0.5">You checked in from outside your assigned geographic zone. Your admin has been notified.</p>
                </div>
              </div>
            )}

            {/* Checkout */}
            {checkedOut ? (
              <div className="mt-4 bg-[#026536]/5 rounded-xl p-3 text-center">
                <p className="text-xs font-semibold text-[#026536]">Checked out for the day</p>
                {todayRecord.checkout_timestamp && (
                  <p className="text-xs text-[#1a2529]/50 mt-0.5">
                    at {formatTime(todayRecord.checkout_timestamp)}
                  </p>
                )}
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <h3 className="text-sm font-bold text-[#1a2529]">Check Out</h3>

                {/* Checkout Selfie */}
                {checkoutSelfie ? (
                  <div className="relative w-20 h-20 mx-auto">
                    <img src={checkoutSelfie} alt="Checkout selfie" className="w-20 h-20 rounded-full object-cover border-2 border-[#1a2529]" />
                    <button data-testid="remove-checkout-selfie" onClick={() => setCheckoutSelfie(null)}
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                      <X size={10} />
                    </button>
                  </div>
                ) : showCheckoutCamera ? (
                  <div>
                    <video ref={checkoutVideoRef} autoPlay playsInline muted className="w-full h-40 object-cover rounded-xl bg-black" />
                    <div className="flex gap-2 mt-2">
                      <Button data-testid="capture-checkout-selfie" onClick={captureCheckoutPhoto}
                        className="flex-1 h-9 bg-[#1a2529] hover:bg-[#1a2529]/90 text-white font-medium rounded-xl text-xs">
                        <Camera size={14} className="mr-1.5" /> Capture
                      </Button>
                      <Button data-testid="cancel-checkout-camera" onClick={stopCamera} variant="outline" className="h-9 rounded-xl text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="open-checkout-camera" onClick={startCheckoutCamera}
                    className="w-full flex items-center gap-3 h-12 bg-[#1a2529]/5 hover:bg-[#1a2529]/8 rounded-xl text-[#1a2529]/60 text-xs font-semibold transition-colors px-4">
                    <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
                      <Camera size={14} className="text-[#1a2529]/50" />
                    </div>
                    Take Checkout Selfie
                  </button>
                )}

                {/* Checkout Location */}
                {checkoutLoc ? (
                  <div className="bg-[#1a2529]/5 border border-[#1a2529]/10 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <MapPin size={14} className="text-[#1a2529]/60 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#1a2529]/70">Checkout location captured</p>
                        <p className="text-[11px] text-[#1a2529]/40 mt-0.5 truncate">{checkoutAddr}</p>
                      </div>
                      <button data-testid="refresh-checkout-location" onClick={getCheckoutLocation}
                        className="text-[11px] text-[#1a2529]/60 font-semibold hover:underline shrink-0">Refresh</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="get-checkout-location" onClick={() => { if (!checkoutLocLoading) getCheckoutLocation(); }} disabled={checkoutLocLoading}
                    className="w-full flex items-center gap-3 h-12 bg-[#f4f4f5] hover:bg-[#1a2529]/8 rounded-xl text-[#1a2529]/60 font-semibold text-xs transition-colors px-4 disabled:opacity-50">
                    <div className="h-8 w-8 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
                      {checkoutLocLoading ? <Loader2 className="animate-spin text-[#1a2529]/50" size={14} /> : <MapPin size={14} className="text-[#1a2529]/50" />}
                    </div>
                    {checkoutLocLoading ? 'Fetching...' : 'Get Checkout Location'}
                  </button>
                )}

                <Button
                  data-testid="checkout-btn"
                  onClick={handleCheckout}
                  disabled={!checkoutLoc || checkoutLoading}
                  className="w-full h-11 bg-[#1a2529] hover:bg-[#1a2529]/90 text-white font-semibold rounded-xl active:scale-[0.98] transition-all disabled:opacity-40"
                >
                  {checkoutLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <LogOut className="mr-2" size={16} />}
                  Check Out
                </Button>
              </div>
            )}
          </div>

          {/* Location Trail */}
          {checkedIn && !checkedOut && (
            <div data-testid="location-log-card" className="bg-white rounded-2xl shadow-sm p-5 animate-slide-up">
              <h3 className="text-sm font-bold text-[#1a2529] flex items-center gap-2 mb-3">
                <Navigation size={15} className="text-[#026536]" /> Log Field Location
              </h3>
              <p className="text-[11px] text-[#1a2529]/40 mb-3">Track your movement throughout the day. Check-in and check-out times are preserved.</p>
              <div className="flex gap-2 mb-3">
                <input
                  data-testid="location-note-input"
                  value={logNote}
                  onChange={(e) => setLogNote(e.target.value)}
                  placeholder="Optional note (e.g. Site visit, Client meeting)..."
                  className="flex-1 text-xs border border-border/60 rounded-lg px-3 py-2.5 outline-none focus:ring-1 focus:ring-[#026536]/30"
                />
                <Button
                  data-testid="log-location-btn"
                  onClick={handleLogLocation}
                  disabled={logLocLoading}
                  className="h-10 bg-[#026536] hover:bg-[#026536]/90 text-white font-semibold rounded-xl gap-1.5 px-4"
                >
                  {logLocLoading ? <Loader2 className="animate-spin" size={14} /> : <MapPin size={14} />}
                  Log
                </Button>
              </div>
              {locationLogs.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-[#1a2529]/40 font-semibold uppercase">Today's Trail ({locationLogs.length})</p>
                  {locationLogs.map((log, idx) => (
                    <div key={log.id} data-testid={`location-log-${idx}`} className="flex items-start gap-2 bg-[#f4f4f5] rounded-lg p-2.5">
                      <div className="h-5 w-5 rounded-full bg-[#026536]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[9px] font-bold text-[#026536]">{idx + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#1a2529]/40 font-medium">{formatTime(log.timestamp)}</span>
                          {log.is_outside_zone && <span className="text-[9px] text-red-500 font-semibold">Outside Zone</span>}
                        </div>
                        {log.note && <p className="text-[11px] text-[#1a2529] font-medium mt-0.5">{log.note}</p>}
                        <p className="text-[10px] text-[#1a2529]/40 truncate">{log.address || `${log.latitude.toFixed(5)}, ${log.longitude.toFixed(5)}`}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </> ) : checkinBlocked && (checkinBlocked.is_non_working || checkinBlocked.is_holiday) ? (
          <div data-testid="non-working-day-card" className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-6 animate-slide-up">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center">
                <AlertCircle className="text-red-400" size={28} />
              </div>
              <div>
                <p className="font-bold text-[#1a2529]">{checkinBlocked.reason}</p>
                <p className="text-xs text-[#1a2529]/50 mt-1">
                  {checkinBlocked.is_non_working
                    ? t('non_working.work_permit_hint')
                    : t('non_working.holiday_hint')}
                </p>
              </div>
              <Button
                data-testid="request-work-permit-btn"
                variant="outline"
                onClick={() => navigate('/employee/leaves')}
                className="mt-2 border-[#026536]/20 text-[#026536] font-semibold rounded-xl h-10"
              >
                {t('leave.request_work_permit')}
              </Button>
            </div>
          </div>
        ) : (
          <div data-testid="checkin-card" className="bg-white rounded-2xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] p-6 animate-slide-up">
            <h2 className="text-base font-bold text-[#1a2529] mb-5">{t('attendance.mark_attendance')}</h2>

            {/* Selfie */}
            <div className="mb-4">
              {selfie ? (
                <div className="relative w-24 h-24 mx-auto">
                  <img src={selfie} alt="Selfie" className="w-24 h-24 rounded-full object-cover border-2 border-[#026536]" />
                  <button
                    data-testid="remove-selfie-btn"
                    onClick={() => setSelfie(null)}
                    className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : showCamera ? (
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-48 object-cover rounded-xl bg-black"
                  />
                  <div className="flex gap-2 mt-2">
                    <Button
                      data-testid="capture-selfie-btn"
                      onClick={capturePhoto}
                      className="flex-1 h-10 bg-[#026536] hover:bg-[#026536]/90 text-white font-medium rounded-xl"
                    >
                      <Camera size={16} className="mr-1.5" /> Capture
                    </Button>
                    <Button
                      data-testid="cancel-camera-btn"
                      onClick={stopCamera}
                      variant="outline"
                      className="h-10 rounded-xl"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="open-camera-btn"
                  onClick={startCamera}
                  className="w-full flex items-center gap-3 h-14 bg-[#026536]/5 hover:bg-[#026536]/10 rounded-xl text-[#026536] text-sm font-semibold transition-colors px-4"
                >
                  <div className="h-9 w-9 rounded-lg bg-[#026536]/10 flex items-center justify-center shrink-0">
                    <Camera size={18} className="text-[#026536]" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-[#026536]">{t('attendance.take_selfie')}</p>
                    <p className="text-[10px] text-[#026536]/50 font-medium">Required</p>
                  </div>
                </button>
              )}
            </div>

            {/* Location */}
            {location ? (
              <div className="bg-[#026536]/5 border border-[#026536]/10 rounded-xl p-3 mb-4">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-[#026536] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#026536]">{t('attendance.location_captured')}</p>
                    <p className="text-xs text-[#1a2529]/60 mt-0.5 truncate">{address}</p>
                  </div>
                  <button
                    data-testid="refresh-location-btn"
                    onClick={getLocation}
                    className="text-xs text-[#026536] font-semibold hover:underline shrink-0"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            ) : (
              <button
                data-testid="get-location-btn"
                onClick={() => { if (!locLoading) getLocation(); }}
                disabled={locLoading}
                className="w-full flex items-center gap-3 h-14 bg-[#f4f4f5] hover:bg-[#1a2529]/8 rounded-xl mb-4 text-[#1a2529]/70 font-semibold text-sm transition-colors px-4 disabled:opacity-50"
              >
                <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm">
                  {locLoading ? <Loader2 className="animate-spin text-[#026536]" size={18} /> : <MapPin size={18} className="text-[#026536]" />}
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-[#1a2529]/70">{locLoading ? t('attendance.fetching_location') : t('attendance.tap_location')}</p>
                  <p className="text-[10px] text-[#1a2529]/30 font-medium">Required</p>
                </div>
              </button>
            )}

            {locError && <p data-testid="location-error" className="text-xs text-red-500 mb-3">{locError}</p>}

            <Button
              data-testid="checkin-btn"
              onClick={handleCheckin}
              disabled={!location || !selfie || checkinLoading || isPending || isRejected}
              className="w-full h-14 bg-[#026536] hover:bg-[#026536]/90 text-white font-bold text-base rounded-xl shadow-lg shadow-[#026536]/25 active:scale-[0.98] transition-all disabled:opacity-40"
            >
              {checkinLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <CheckCircle2 className="mr-2" size={18} />}
              {checkinLoading ? t('attendance.marking') : t('attendance.mark_present')}
            </Button>
          </div>
        )}

        <div className="text-center">
          <p className="text-xs text-[#1a2529]/40 font-medium">
            {formatDate(new Date().toISOString())}
          </p>
        </div>
      </div>

      {showConsent && <ConsentModal onAccepted={() => setShowConsent(false)} />}
      <BottomNav />
    </div>
  );
}
